import { css } from '@styled-system/css';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';

// Renommage rapide d'une recherche depuis le menu ⋯ de l'historique.

export interface RenameSearchModalProps {
  isOpen: boolean;
  initialName: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}

const formGroup = css({ display: 'flex', flexDirection: 'column', gap: '4', paddingTop: '4' });

const errorText = css({ fontSize: 'bodySm', color: 'error.default' });

export const RenameSearchModal: FC<RenameSearchModalProps> = ({ isOpen, initialName, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError(null);
    }
  }, [isOpen, initialName]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('folders.errorRequired'));
      return;
    }
    setSaving(true);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('folders.renameSearchTitle')}
      width="420px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" handleClick={handleSubmit} showLoader>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className={formGroup}>
        <Input
          autoFocus
          value={name}
          state={error ? 'error' : 'default'}
          onChange={(event) => {
            setName(event.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleSubmit();
            }
          }}
        />
        {error && <span className={errorText}>{error}</span>}
      </div>
    </Modal>
  );
};
